import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { IAuthorizationClass } from '../lookups/IAuthorizationClass';
import { IAuthorizationType } from '../lookups/IAuthorizationType';
import { IDirectReport } from '../lookups/IDirectReport';
import { IServiceItem } from '../lookups/IServiceItem';
import { ITask } from '../lookups/ITask';
import { LoggingService } from '../shared/services/logging/logging.service';
import { IUserAuthorizationResponse, UserAuthorization } from './store';
import { PendingAuthorization } from './store/pending-authorization.model';

@Injectable({
    providedIn: 'root'
})
export class UserAuthorizationsService {
    private uri = `${environment.apiLocation}/Authorization`;
    private pendingCountUri = `${environment.apiLocation}/AuthPending`;
    private pendingAuthsUri = `${environment.apiLocation}/AuthPrep`;
    constructor(
        private http: HttpClient,
        private log: LoggingService
    ) {}
    // TODO: fix the case mismatches
    buildPostRequest(params: {
        // endDate?: Date;
        directReports?: Array<Partial<IDirectReport>>;
        tasks?: Array<Partial<ITask>>;
        serviceItems?: Array<Partial<IServiceItem>>;
        classes?: Array<Partial<IAuthorizationClass>>;
        authTypes?: Array<Partial<IAuthorizationType>>;
        modifiedDate?: Date;
        endDateOperator?: string;
        modifiedDateOperator?: string;
        AddAuthMode?: boolean;
        AddAuthAction?: boolean;
        showSI?: boolean;
        showPI?: boolean;
        EmployeeAuthStartDate?: Date;
        EmployeeAuthEndDate?: Date;
    }): {
        ModifiedDateTime: string;
        DirectReports: Array<Partial<IDirectReport>>;
        Tasks: Array<Partial<ITask>>;
        ServiceItems: Array<Partial<IServiceItem>>;
        Classes: Array<Partial<IAuthorizationClass>>;
        AuthTypes: Array<Partial<IAuthorizationType>>;
        EndDateOperator: string;
        ModifiedDateOperator: string;
        AddAuthAction?: boolean;
        AddAuthMode?: boolean;
        ShowSI?: boolean;
        ShowPI?: boolean;
        EmployeeAuthStartDate?: string;
        EmployeeAuthEndDate?: string;
    } {
        const defaults = {
            endDateOperator: 'GE',
            // params.AddAuthMode === true
            //     ? params.EmployeeAuthEndDate
            //         ? 'GE'
            //         : null
            //     : 'GE',
            modifiedDateOperator: undefined,
            // endDate: moment().format('YYYY-MM-DD'),
            modifiedDate: undefined,
            directReports: [],
            tasks: [],
            serviceItems: [],
            classes: [],
            authTypes: [],
            AddAuthAction: false,
            AddAuthMode: true,
            // EmployeeAuthStartDate: moment().format('YYYY-MM-01'),
            EmployeeAuthStartDate: undefined,
            EmployeeAuthEndDate:
                params.AddAuthMode === true
                    ? undefined
                    : moment(params.EmployeeAuthEndDate).format('YYYY-MM-DD') ??
                      moment().format('YYYY-MM-DD')
        };

        const temp = {
            endDateOperator: params.endDateOperator || defaults.endDateOperator,
            modifiedDateOperator:
                params.modifiedDateOperator || defaults.modifiedDateOperator,
            // endDate: params.endDate || defaults.endDate,
            modifiedDate: params.modifiedDate || defaults.modifiedDate,
            directReports: params.directReports || defaults.directReports,
            tasks: params.tasks || defaults.tasks,
            serviceItems: params.serviceItems || defaults.serviceItems,
            classes: params.classes || defaults.classes,
            authTypes: params.authTypes || defaults.authTypes,
            AddAuthAction: params.AddAuthAction || defaults.AddAuthAction,
            AddAuthMode: params.AddAuthMode || defaults.AddAuthMode,
            showSI: params.showSI,
            showPI: params.showPI,
            EmployeeAuthStartDate: params.EmployeeAuthStartDate
                ? moment(params.EmployeeAuthStartDate).format('YYYY-MM-DD')
                : defaults.EmployeeAuthStartDate,
            EmployeeAuthEndDate: params.EmployeeAuthEndDate
                ? moment(params.EmployeeAuthEndDate).format('YYYY-MM-DD')
                : defaults.EmployeeAuthEndDate
        };

        const request = {
            // TODO: what's the create date param?
            ModifiedDateTime: temp.modifiedDate
                ? moment(temp.modifiedDate).format('YYYY-MM-DD')
                : null,
            DirectReports: temp.directReports,
            Tasks: temp.tasks,
            // Tasks: temp.tasks,
            ServiceItems: temp.serviceItems,
            // ServiceItems: temp.serviceItems,
            Classes: temp.classes,
            AuthTypes: temp.authTypes,
            EndDateOperator: temp.endDateOperator,
            ModifiedDateOperator: temp.modifiedDateOperator,
            AddAuthAction: temp.AddAuthAction,
            AddAuthMode: temp.AddAuthMode,
            ShowSI: temp.showSI,
            ShowPI: temp.showPI,
            EmployeeAuthStartDate: temp.EmployeeAuthStartDate,
            EmployeeAuthEndDate: temp.EmployeeAuthEndDate
        };

        this.log.debug({
            type: 'UserAuthorizationsService:buildPostRequest',
            defaults,
            request,
            params,
            temp
        });
        // if (!params.EmployeeAuthStartDate) {
        //     delete request.EmployeeAuthStartDate;
        // }
        return request;
    }
    mapAuthorization(item: UserAuthorization): UserAuthorization {
        return {
            ...item,
            id: item.EmpAuthID,
            EmpAuthEndDate: moment(item.EmpAuthEndDate).isValid()
                ? moment(item.EmpAuthEndDate).toDate()
                : null,
            EmpAuthStartDate: moment(item.EmpAuthStartDate).isValid()
                ? moment(item.EmpAuthStartDate).toDate()
                : null,
            ModifiedDateTime: moment(item.ModifiedDateTime).isValid()
                ? moment(item.ModifiedDateTime).toDate()
                : null
        } as UserAuthorization;
    }
    mapAuthorizations(items: UserAuthorization[]): UserAuthorization[] {
        this.log.verbose({
            type: 'UserAuthorizationsService:mapAuthorizations',
            this: this
        });
        return items.map(item => this.mapAuthorization(item));
    }
    public getUserAuthorizations(params: {
        endDate?: Date;
        directReports?: Array<IDirectReport>;
        tasks?: Array<ITask>;
        serviceItems?: Array<IServiceItem>;
        classes?: Array<IAuthorizationClass>;
        authTypes?: Array<IAuthorizationType>;
        modifiedDate?: Date;
        endDateOperator?: string;
        modifiedDateOperator?: string;
        AddAuthAction?: boolean;
        AddAuthMode?: boolean;
        showSI?: boolean;
        showPI?: boolean;
    }): Observable<Array<UserAuthorization>> {
        this.log.debug({
            type: 'UserAuthorizationsService:getUserAuthorizations',
            params
        });

        const request = this.buildPostRequest(params);

        this.log.verbose({
            type: 'UserAuthorizationsService:getUserAuthorizations',
            uri: this.uri,
            request
        });
        return this.http.post<UserAuthorization[]>(this.uri, request).pipe(
            map(items => this.mapAuthorizations(items)),
            tap(data =>
                this.log.verbose({
                    type: 'UserAuthorizationsService:getUserAuthorizations:reponse',
                    data
                })
            )
        );
    }

    public putUserAuthorizations(
        authorizations: Array<UserAuthorization>
    ): Observable<IUserAuthorizationResponse> {
        return this.http
            .put<IUserAuthorizationResponse>(this.uri, {
                Authorizations: authorizations
                    // TODO: remove the enddateoperator when it is removed from the API
                    .map(this.mapAuthorization)
                    .map(a => ({
                        ...a,
                        EndDateOperator: 'XX',
                        ClassListID: a.ClassListID || 'na'
                    }))
            })
            .pipe(
                map(resp => ({
                    ...resp,
                    Items: this.mapAuthorizations(resp.Items)
                })),
                tap(data =>
                    this.log.verbose({
                        type: 'UserAuthorizationsService:putUserAuthorizations:reponse',
                        data
                    })
                )
            );
    }
    public deleteAuthorizations(
        authorizations: Array<number>
    ): Observable<IUserAuthorizationResponse> {
        this.log.verbose({
            type: 'UserAuthorizationsService:deleteAuthorizations',
            uri: this.uri,
            authorizations
        });

        return (
            this.http
                // TODO: change to .delete<IResponse> when upgrading to NG7
                .request<IUserAuthorizationResponse>('DELETE', this.uri, {
                    body: { EntityIDs: authorizations }
                })
                .pipe(
                    map(res => {
                        return {
                            ...res,
                            Items: this.mapAuthorizations(res.Items)
                        };
                    }),
                    tap(data =>
                        this.log.verbose({
                            type: 'UserAuthorizationsService:deleteAuthorizations:reponse',
                            data
                        })
                    )
                )
        );
    }
    public getPendingAuthorizationsCount(): Observable<number> {
        this.log.verbose({
            type: 'UserAuthorizationsService:getPendingAuthorizationsCount'
        });
        return this.http
            .get<{ AuthPending: number }>(this.pendingCountUri)
            .pipe(map(x => x.AuthPending));
    }
    public addPendingAuthorizations(params: {
        endDate: Date;
        endDateOperator: string;
        directReports: Array<Partial<IDirectReport>>;
        tasks: Array<Partial<ITask>>;
        serviceItems: Array<Partial<IServiceItem>>;
        classes: Array<Partial<IAuthorizationClass>>;
        authTypes: Array<Partial<IAuthorizationType>>;
        modifiedDate: Date;
        AddAuthMode?: boolean;
        AddAuthAction?: boolean;
        showSI?: boolean;
        EmployeeAuthStartDate?: Date;
        EmployeeAuthEndDate?: Date;
    }): Observable<UserAuthorization[]> {
        const request: any = this.buildPostRequest(params);
        request.AddAuthMode = true;
        request.AddAuthAction = true;

        this.log.verbose({
            type: 'UserAuthorizationsService:addPendingAuthorizations',
            uri: this.uri,
            request
        });

        return this.http.post<Array<UserAuthorization>>(this.uri, request).pipe(
            map(items => this.mapAuthorizations(items)),
            tap(data =>
                this.log.verbose({
                    type: 'UserAuthorizationsService:addPendingAuthorizations:reponse',
                    data
                })
            )
        );
    }
    public getPendingAuthorizations(): Observable<PendingAuthorization[]> {
        return this.http.get<PendingAuthorization[]>(this.pendingAuthsUri);
    }
}
